import { ReactNode } from '@tanstack/react-router'
import { Slider } from 'radix-ui'
import { useMemo, useRef } from 'react'
import { clampNumber, roundToStep } from '../support/number-step'
import { usePermissiveNumberInput } from '../support/use-permissive-number-input'

export type WidgetInputSliderFieldProps = {
	value: number
	onChange: (value: number) => void
	/** When set, takes every value during a drag and `onChange` fires only on release. */
	onTransientChange?: (value: number) => void
	label?: ReactNode
	min?: number
	max?: number
	step?: number
	suffix?: ReactNode
}

export function WidgetInputSliderField(
	{
		value,
		onChange,
		onTransientChange,
		label,
		min: minProp,
		max: maxProp,
		step: stepProp,
		suffix,
	}: WidgetInputSliderFieldProps,
) {
	const min = minProp ?? 0
	const max = maxProp ?? 100
	const step = stepProp ?? 1
	// `label` is a ReactNode so callers can render markup; only a string can name a control.
	const ariaLabel = typeof label === 'string' ? label : undefined
	// Read on cancellation, where the `value` prop may not have caught up to the
	// last value the drag emitted.
	const uncommittedDragValue = useRef<number | null>(null)

	const clampedValue = useMemo(() => {
		if (value < min) { return min }
		if (value > max) { return max }
		return value
	}, [value, min, max])

	const sliderValue = useMemo(() => [clampedValue], [clampedValue])

	const { internalValue, onInternalChange, onFocus, onBlur } = usePermissiveNumberInput({
		value: clampedValue,
		onChange,
		// Snapping here rather than in the caller's onChange is load-bearing: the hook
		// only stops re-emitting once this returns the value that came back in `value`.
		parse: (next: number) => {
			if (Number.isNaN(next)) { return NaN }
			return clampNumber(roundToStep(next, step), min, max)
		},
	})

	return (
		<div className="grid grid-cols-[1fr_auto] items-center gap-1">
			<div className="grid items-center gap-1">
				<Slider.Root
					className="relative flex items-center select-none touch-none w-full h-5 min-w-15"
					value={sliderValue}
					onValueChange={(values: number[]) => {
						uncommittedDragValue.current = values[0]
						const emitDragChange = onTransientChange ?? onChange
						emitDragChange(values[0])
					}}
					onValueCommit={(values: number[]) => {
						uncommittedDragValue.current = null
						if (onTransientChange) { onChange(values[0]) }
					}}
					onPointerCancel={() => {
						// Radix ends a slide from pointerup only, so an interrupted drag
						// would otherwise leave the last transient value uncommitted.
						const pending = uncommittedDragValue.current
						uncommittedDragValue.current = null
						if (onTransientChange && pending !== null) { onChange(pending) }
					}}
					max={max}
					min={min}
					step={step}
				>
					<Slider.Track className="bg-grey-200 relative grow rounded-full h-[2px]">
						<Slider.Range className="absolute bg-blue-500 rounded-full h-full" />
					</Slider.Track>
					<Slider.Thumb
						aria-label={ariaLabel}
						className="block w-3 h-3 bg-white border-2 border-blue-500 rounded-full hover:bg-grey-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-2"
					/>
				</Slider.Root>
			</div>
			<div>
				<input
					type="text"
					inputMode="numeric"
					aria-label={ariaLabel && `${ariaLabel} value`}
					value={internalValue}
					onChange={(e) => onInternalChange(e.target.value)}
					onFocus={onFocus}
					onBlur={onBlur}
					min={min}
					max={max}
					className="w-6 text-center"
				/>
				{suffix}
			</div>
		</div>
	)
}
